import {ref} from 'vue';
import {defineStore} from 'pinia';
import {useLogger, useTranslation} from '#imports';
import {readonlyState} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {SkillSuggestionTypeEnum} from '@api';
import {Configuration, type Skill, SkillOriginEnum, type SkillSuggestions, SkillSuggestionsApi, SkillTypeEnum} from '@api';

export const useSkillsSuggestionsStore = defineStore('skills-suggestions-store', () => {
    const _apiConfiguration = new Configuration({basePath: ''});
    const skillsSuggestionsApi = new SkillSuggestionsApi(_apiConfiguration);
    const logger = useLogger();
    const {t} = useTranslation();

    const suggestedSkills = ref<Skill[]>([]);
    const loading = ref<boolean>(false);

    async function suggestSkills(description: string): Promise<Skill[]> {
        try {
            loading.value = true;
            const response = await skillsSuggestionsApi.suggestSkills({skillSuggestionsRequest: {description}});
            suggestedSkills.value = mapResponseToSkills(response);
            return suggestedSkills.value;
        } catch (e) {
            logger.error('Failed to suggest skills:', e);
            pushVJoySnackbar({props: {message: t('generic.error.unexpected'), level: 'error'}});
            suggestedSkills.value = [];
            return [];
        } finally {
            loading.value = false;
        }
    }

    function resetSuggestedSkills() {
        suggestedSkills.value = [];
    }

    function mapResponseToSkills(skillSuggestions: SkillSuggestions): Skill[] {
        return (
            skillSuggestions?.suggestedSkills?.map((skillSuggestion) => ({
                id: skillSuggestion.id,
                label: skillSuggestion.label,
                type: mapSkillType(skillSuggestion.type),
                origin: SkillOriginEnum.Suggestion,
            })) ?? []
        );
    }

    function mapSkillType(skillType: SkillSuggestionTypeEnum): SkillTypeEnum {
        switch (skillType) {
            case 'FUNCTIONAL':
                return SkillTypeEnum.Functional;
            case 'GLOBAL':
                return SkillTypeEnum.Global;
        }
    }

    return {
        suggestedSkills: readonlyState(suggestedSkills),
        loading: readonlyState(loading),
        suggestSkills,
        resetSuggestedSkills,
    };
});
